// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { Patient } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import type { AppointmentTypeDefinition } from '@polarissleep/scheduling';
import { SCHEDULING_ERROR_CATEGORIES, type SchedulingError } from '@polarissleep/scheduling-next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { findPatientAppointments } from './availability';
import type { PatientAppointmentCandidate } from './types';

const APPOINTMENTS_CACHE_TTL_MS = 30_000;
const MAX_APPOINTMENTS_CACHE_ENTRIES = 24;
const MAX_AVAILABILITY_ATTEMPTS = 2;
const AVAILABILITY_RETRY_DELAY_MS = 250;
const SCHEDULING_ENGINE = 'medplum-v2';

interface AppointmentCacheEntry {
  appointments: PatientAppointmentCandidate[];
  expiresAt: number;
}

const appointmentsCache = new Map<string, AppointmentCacheEntry>();

interface UseAvailableAppointmentsOptions {
  appointmentType: AppointmentTypeDefinition | undefined;
  practitionerId?: string;
  startDate: string | undefined;
  endDate: string | undefined;
}

export function useAvailableAppointments({
  appointmentType,
  practitionerId,
  startDate,
  endDate,
}: UseAvailableAppointmentsOptions): {
  appointments: PatientAppointmentCandidate[];
  loading: boolean;
  error: Error | null;
  ready: boolean;
  refresh: () => void;
} {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient | undefined;
  const [appointments, setAppointments] = useState<PatientAppointmentCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [loadedCacheKey, setLoadedCacheKey] = useState<string | undefined>();
  const requestSequence = useRef(0);
  const activeController = useRef<AbortController | undefined>(undefined);
  const cacheKey =
    profile?.id && appointmentType?.coding.code && startDate && endDate
      ? [
          SCHEDULING_ENGINE,
          profile.id,
          appointmentType.coding.code,
          practitionerId ?? 'eligible',
          startDate,
          endDate,
        ].join('|')
      : undefined;

  const fetchAppointments = useCallback(
    async (force = false) => {
      const sequence = requestSequence.current + 1;
      requestSequence.current = sequence;
      activeController.current?.abort();

      if (!profile || !appointmentType || !startDate || !endDate) {
        activeController.current = undefined;
        setAppointments([]);
        setError(null);
        setLoadedCacheKey(undefined);
        setLoading(false);
        return;
      }

      if (cacheKey && !force) {
        const cached = readCachedAppointments(cacheKey);
        if (cached !== undefined) {
          setAppointments(cached);
          setError(null);
          setLoadedCacheKey(cacheKey);
          setLoading(false);
          return;
        }
      }

      setLoading(true);
      setError(null);
      setLoadedCacheKey(undefined);
      const controller = new AbortController();
      activeController.current = controller;
      try {
        let result: PatientAppointmentCandidate[] | undefined;
        let lastError: unknown;
        for (let attempt = 0; attempt < MAX_AVAILABILITY_ATTEMPTS; attempt += 1) {
          if (controller.signal.aborted) {
            return;
          }
          try {
            result = await findPatientAppointments(medplum, {
              patient: profile,
              appointmentType,
              startDate,
              endDate,
              practitionerId,
              signal: controller.signal,
            });
            break;
          } catch (err: unknown) {
            if (controller.signal.aborted) {
              return;
            }
            lastError = err;
            if (attempt + 1 < MAX_AVAILABILITY_ATTEMPTS) {
              await new Promise<void>((resolve) => {
                const timeoutId = window.setTimeout(resolve, AVAILABILITY_RETRY_DELAY_MS);
                controller.signal.addEventListener(
                  'abort',
                  () => {
                    window.clearTimeout(timeoutId);
                    resolve();
                  },
                  { once: true }
                );
              });
            }
          }
        }
        if (!result) {
          throw lastError instanceof Error ? lastError : new Error('Unable to load appointments.');
        }
        const deduped = dedupeCandidates(result);
        if (sequence !== requestSequence.current || controller.signal.aborted) {
          return;
        }
        if (cacheKey) {
          writeCachedAppointments(cacheKey, deduped);
        }
        setLoadedCacheKey(cacheKey);
        setAppointments(deduped);
      } catch (err) {
        if (sequence !== requestSequence.current || controller.signal.aborted) {
          return;
        }
        setError(toPatientSchedulingError(err));
        setLoadedCacheKey(cacheKey);
      } finally {
        if (sequence === requestSequence.current) {
          activeController.current = undefined;
          setLoading(false);
        }
      }
    },
    [appointmentType, cacheKey, endDate, medplum, practitionerId, profile, startDate]
  );

  useEffect(() => {
    fetchAppointments().catch(console.error);
    return () => {
      requestSequence.current += 1;
      activeController.current?.abort();
      activeController.current = undefined;
    };
  }, [fetchAppointments]);

  useEffect(() => {
    if (!cacheKey || loadedCacheKey !== cacheKey) {
      return;
    }

    const cached = appointmentsCache.get(cacheKey);
    if (!cached) {
      return;
    }

    const timeout = setTimeout(
      () => {
        appointmentsCache.delete(cacheKey);
        fetchAppointments(true).catch(console.error);
      },
      Math.max(cached.expiresAt - Date.now(), 0)
    );

    return () => clearTimeout(timeout);
  }, [cacheKey, fetchAppointments, loadedCacheKey]);

  return {
    appointments,
    loading,
    error,
    ready: Boolean(cacheKey && loadedCacheKey === cacheKey && !loading && !error),
    refresh: () => {
      fetchAppointments(true).catch(console.error);
    },
  };
}

function readCachedAppointments(cacheKey: string): PatientAppointmentCandidate[] | undefined {
  const entry = appointmentsCache.get(cacheKey);
  if (!entry) {
    return undefined;
  }
  if (entry.expiresAt <= Date.now()) {
    appointmentsCache.delete(cacheKey);
    return undefined;
  }
  return entry.appointments;
}

function writeCachedAppointments(cacheKey: string, appointments: PatientAppointmentCandidate[]): void {
  pruneAppointmentsCache();
  while (appointmentsCache.size >= MAX_APPOINTMENTS_CACHE_ENTRIES) {
    const oldestKey = appointmentsCache.keys().next().value;
    if (!oldestKey) {
      break;
    }
    appointmentsCache.delete(oldestKey);
  }
  appointmentsCache.set(cacheKey, { appointments, expiresAt: Date.now() + APPOINTMENTS_CACHE_TTL_MS });
}

function pruneAppointmentsCache(): void {
  const now = Date.now();
  for (const [key, entry] of appointmentsCache) {
    if (entry.expiresAt <= now) {
      appointmentsCache.delete(key);
    }
  }
}

function toPatientSchedulingError(error: unknown): Error {
  if (error instanceof Error && error.name === 'AbortError') {
    return error;
  }

  const category = isSchedulingError(error) ? error.category : undefined;
  let message = 'Appointments are temporarily unavailable. Please try again later.';
  if (category === SCHEDULING_ERROR_CATEGORIES.timeUnavailable) {
    message = 'No appointments are available for that time. Try another date.';
  } else if (category === SCHEDULING_ERROR_CATEGORIES.appointmentAlreadyBooked) {
    message = 'That appointment is no longer available. Please choose another time.';
  }
  return new Error(message);
}

function isSchedulingError(error: unknown): error is SchedulingError {
  return error instanceof Error && 'category' in error;
}

function dedupeCandidates(candidates: PatientAppointmentCandidate[]): PatientAppointmentCandidate[] {
  const seen = new Set<string>();
  return candidates.filter((candidate) => {
    if (seen.has(candidate.key)) {
      return false;
    }
    seen.add(candidate.key);
    return true;
  });
}
